import { useRef, useEffect, useState, ReactNode } from 'react'
import { motion } from 'framer-motion'
import { useDesktopStore, WindowId } from '@/stores/desktopStore'
import './Window.css'

interface WindowProps {
  id: WindowId
  children: ReactNode
  className?: string
}

export function Window({ id, children, className = '' }: WindowProps) {
  const { windows, closeWindow, focusWindow, updateWindowPosition } = useDesktopStore()
  const windowState = windows.find((w) => w.id === id)
  const windowRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [prevPosition, setPrevPosition] = useState<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && windowState) {
        const newX = Math.max(0, e.clientX - dragOffset.x)
        const newY = Math.max(0, e.clientY - dragOffset.y)
        updateWindowPosition(id, { x: newX, y: newY })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragOffset, id, updateWindowPosition, windowState])

  if (!windowState || !windowState.isOpen || windowState.isMinimized) {
    return null
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.window__header') && 
        !(e.target as HTMLElement).closest('.window__control')) {
      setIsDragging(true)
      const rect = windowRef.current?.getBoundingClientRect()
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        })
      }
    }
    focusWindow(id)
  }

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    closeWindow(id)
  }

  const handleToggleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isFullscreen) {
      if (prevPosition) {
        updateWindowPosition(id, prevPosition)
      }
      setIsFullscreen(false)
    } else {
      setPrevPosition({ x: windowState.position.x, y: windowState.position.y })
      updateWindowPosition(id, { x: 0, y: 0 })
      setIsFullscreen(true)
    }
  }

  return (
    <motion.div
      ref={windowRef}
      className={`window ${className} ${isFullscreen ? 'window--fullscreen' : ''}`}
      style={{
        left: isFullscreen ? 0 : windowState.position.x,
        top: isFullscreen ? 0 : windowState.position.y,
        width: isFullscreen ? '100vw' : windowState.size.width,
        height: isFullscreen ? '100vh' : windowState.size.height,
        zIndex: windowState.zIndex,
      }}
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      onMouseDown={handleMouseDown}
    >
      <div className="window__header">
        <div className="window__controls">
          <button
            className="window__control window__control--close"
            onClick={handleClose}
            aria-label="Close window"
          >
            <span>×</span>
          </button>
          <button
            className="window__control window__control--maximize"
            onClick={handleToggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            <span>{isFullscreen ? '◱' : '□'}</span>
          </button>
        </div>
        <div className="window__title">
          <span className="window__title-text">{windowState.title}</span>
        </div>
        <div className="window__header-spacer" />
      </div>
      <div className="window__content">{children}</div>
    </motion.div>
  )
}
